import { CheckCircle2, Download, XCircle, AlertTriangle } from "lucide-react";
import { useRef, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import {
  FetchUserDetailAction,
  UpdateUserAction,
} from "../../Store/Actions/UserActions";
import { UserActionConst } from "../../Store/Constants/UserConstants";
import { handleAuthError } from "../../utils/authUtils";
import RejectionModal from "./rejection-modal";

const BACK_PATHS = {
  approve: "/dashboard/users/approve-users",
  nonprofit: "/dashboard/users/nonprofit-users",
  pin: "/dashboard/users/pin-users",
};

const MainUserDetail = ({ from = "approve" }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const backPath = BACK_PATHS[from] ?? BACK_PATHS.approve;
  const dispatch = useDispatch();
  const [isUpdating, setIsUpdating] = useState(false);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const processedUpdateRef = useRef(null);

  const userState = useSelector((state) => state.user);
  const userDetail = userState?.userDetail;
  const loader = userState?.loader ?? false;
  const userDetailError = userState?.userDetailError;
  const updateSuccess = userState?.updateSuccess;
  const updateError = userState?.updateError;

  useEffect(() => {
    if (id) {
      // Reset processed update ref when user ID changes
      processedUpdateRef.current = null;
      dispatch(FetchUserDetailAction(id));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (userDetailError) {
      // Check for auth errors first
      if (handleAuthError(userDetailError, null, dispatch)) {
        return; // Auth error handled, don't show additional toast
      }
      
      const errorMessage =
        userDetailError.message || "Failed to fetch user details.";
      toast.error(errorMessage);
    }
  }, [userDetailError, dispatch]);

  useEffect(() => {
    if (
      updateSuccess &&
      (updateSuccess.statusCode === 200 || updateSuccess.statusCode === 201) &&
      !processedUpdateRef.current
    ) {
      // Mark as processed to prevent duplicate handling
      processedUpdateRef.current = updateSuccess;

      const status = updateSuccess.data?.activationStatus;
      if (status === "ACTIVE") {
        toast.success("User approved successfully!");
      } else if (status === "REJECTED") {
        toast.success("User rejected successfully!");
      } else {
        toast.success("User updated successfully!");
      }
      setIsUpdating(false);

      // Clear the update success state immediately to prevent re-triggering
      dispatch({ type: UserActionConst.CLEAR_UPDATE_STATE });

      // Navigate back to listing after a short delay
      setTimeout(() => {
        navigate(backPath);
      }, 1000);
    }
  }, [updateSuccess, navigate, dispatch, backPath]);

  useEffect(() => {
    if (updateError) {
      // Check for auth errors first
      if (handleAuthError(updateError, null, dispatch)) {
        setIsUpdating(false);
        dispatch({ type: UserActionConst.CLEAR_UPDATE_STATE });
        return; // Auth error handled, don't show additional toast
      }
      
      const errorMessage = updateError.message || "Failed to update user.";
      toast.error(errorMessage);
      setIsUpdating(false);
      // Clear the update error state after handling
      dispatch({ type: UserActionConst.CLEAR_UPDATE_STATE });
    }
  }, [updateError, dispatch]);

  const handleApprove = () => {
    if (!id) return;
    setIsUpdating(true);
    dispatch(
      UpdateUserAction(id, {
        activationStatus: "ACTIVE",
        isApproved: true,
      })
    );
  };

  const handleReject = () => {
    setShowRejectionModal(true);
  };

  const handleRejectionConfirm = (rejectionData) => {
    if (!id) return;
    setShowRejectionModal(false);
    setIsUpdating(true);
    dispatch(
      UpdateUserAction(id, {
        activationStatus: "REJECTED",
        isApproved: false,
        rejectionReasonType: rejectionData.reasonType,
        rejectionDetail: rejectionData.detail,
      })
    );
  };


  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loader && !userDetail) {
    return (
      <div className="w-full lg:w-[80%] bg-white rounded-xl shadow p-3 lg:p-6 flex justify-center items-center py-12">
        <svg
          aria-hidden="true"
          className="w-8 h-8 text-gray-200 animate-spin fill-themeColor"
          viewBox="0 0 100 101"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
            fill="currentColor"
          />
          <path
            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
            fill="currentFill"
          />
        </svg>
      </div>
    );
  }

  if (!userDetail) {
    return (
      <div className="w-full lg:w-[80%] bg-white rounded-xl shadow p-3 lg:p-6">
        <p className="text-textColor font-popinsMedium">User not found</p>
      </div>
    );
  }

  const isNonProfit = !!userDetail.nonProfitUser;
  const organizationName = isNonProfit
    ? userDetail.nonProfitUser.organizationName
    : `${userDetail.firstName || ""} ${userDetail.lastName || ""}`.trim() || "User";
  const roleName = userDetail.roles?.[0]?.name || "N/A";
  const activationStatus = userDetail.activationStatus || "PENDING";

  const getInitials = (firstName = "") => {
  if (!firstName) return "NA";

  // Remove extra spaces & split
  const words = firstName.trim().split(/\s+/);

  // 1 word → first 2 letters
  if (words.length === 1) {
    return words[0].substring(0, 2).toUpperCase();
  }

  // 2 or more words → first 2 words ke first letters
  return (words[0][0] + words[1][0]).toUpperCase();
};

  return (
    <div className="w-full lg:w-[80%] bg-white rounded-xl border-0 p-3 lg:p-6">
      {activationStatus === "INACTIVE" && (
        <div className="flex items-center gap-2 mb-5 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm font-popinsRegular">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <span>This user has deleted their account.</span>
        </div>
      )}
      <div className="flex flex-col lg:flex-row">
        <div className="lg:w-[50%]">
          <h1 className="font-popinsSemiBold text-xl lg:text-2xl">
            {organizationName}
          </h1>

          <div className="mt-6">
            <h3 className="font-popinsSemiBold mb-1">User Type</h3>
            <div className="flex items-center gap-2 mt-2">
              <p className="font-popinsRegular text-[#61758A] text-sm">
                {roleName?.toUpperCase() || "N/A"}
              </p>
              <span
                className={`px-2 py-1 inline-flex text-xs leading-5 font-popinsSemiBold rounded-full ${
                  activationStatus === "ACTIVE"
                    ? "bg-green-100 text-green-800"
                    : activationStatus === "REJECTED"
                    ? "bg-red-100 text-red-800"
                    : activationStatus === "PENDING"
                    ? "bg-yellow-100 text-yellow-800"
                    : "bg-gray-100 text-gray-800"
                }`}
              >
                {activationStatus || "N/A"}
              </span>
            </div>
          </div>

          <div className="mb-6 mt-6">
            <h3 className="font-popinsSemiBold mb-1">Registration Date</h3>
            <p className="font-popinsRegular text-[#61758A] text-sm mt-2">
              {formatDate(userDetail.createdAt)}
            </p>
          </div>

          {isNonProfit ? (
            <>
              {userDetail.nonProfitUser?.address && (
                <div className="mb-6">
                  <h3 className="font-popinsSemiBold mb-1">Location</h3>
                  <div className="font-popinsRegular text-[#61758A] text-sm mt-2">
                    {userDetail.nonProfitUser.address}
                  </div>
                </div>
              )}

              {userDetail.nonProfitUser?.zipCode && (
                <div className="mb-6">
                  <h3 className="font-popinsSemiBold mb-1">Zip Code</h3>
                  <p className="font-popinsRegular text-[#61758A] text-sm mt-2">
                    {userDetail.nonProfitUser.zipCode}
                  </p>
                </div>
              )}

              {userDetail.nonProfitUser?.ein && (
                <div className="mb-6">
                  <h3 className="font-popinsSemiBold mb-1">EIN (Tax ID)</h3>
                  <p className="font-popinsRegular text-[#61758A] text-sm mt-2">
                    {userDetail.nonProfitUser.ein}
                  </p>
                </div>
              )}

              {userDetail.services && userDetail.services.length > 0 && (
                <div className="mb-6">
                  <h3 className="font-popinsSemiBold mb-1">Services Offered</h3>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {userDetail.services.map((service) => (
                      <span
                        key={service.id}
                        className="inline-flex items-center px-2 py-1 bg-[#3FC4E3]/10 text-[#3FC4E3] rounded-md text-xs font-popinsMedium"
                      >
                        {service.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="mb-8">
                <h3 className="font-popinsSemiBold pb-5 border-b border-border">
                  Contact Info
                </h3>
                <div className="flex flex-col lg:flex-row gap-8 mt-4">
                  <div>
                    <h3 className="font-popinsSemiBold mb-1">Email</h3>
                    <p className="font-popinsRegular text-[#61758A] text-sm mt-2">
                      {userDetail.email || "-"}
                    </p>
                  </div>
                  {userDetail.nonProfitUser?.phoneNumber && (
                    <div>
                      <h3 className="font-popinsSemiBold mb-1">Phone</h3>
                      <p className="font-popinsRegular text-[#61758A] text-sm mt-2">
                        {userDetail.nonProfitUser.phoneNumber}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <>
              {(userDetail.firstName || userDetail.lastName) && (
                <div className="mb-6">
                  <h3 className="font-popinsSemiBold mb-1">Full Name</h3>
                  <p className="font-popinsRegular text-[#61758A] text-sm mt-2">
                    {`${userDetail.firstName || ""} ${userDetail.lastName || ""}`.trim() || "-"}
                  </p>
                </div>
              )}

              {userDetail.zipCode && (
                <div className="mb-6">
                  <h3 className="font-popinsSemiBold mb-1">Zip Code</h3>
                  <p className="font-popinsRegular text-[#61758A] text-sm mt-2">
                    {userDetail.zipCode}
                  </p>
                </div>
              )}

              {userDetail.incomeLevel && (
                <div className="mb-6">
                  <h3 className="font-popinsSemiBold mb-1">Income Level</h3>
                  <p className="font-popinsRegular text-[#61758A] text-sm mt-2">
                    {userDetail.incomeLevel}
                  </p>
                </div>
              )}

              {userDetail.numberOfPeople != null && (
                <div className="mb-6">
                  <h3 className="font-popinsSemiBold mb-1">Number of People in Household</h3>
                  <p className="font-popinsRegular text-[#61758A] text-sm mt-2">
                    {userDetail.numberOfPeople}
                  </p>
                </div>
              )}

              {userDetail.supportRequired && userDetail.supportRequired.length > 0 && (
                <div className="mb-6">
                  <h3 className="font-popinsSemiBold mb-1">Support Required</h3>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {userDetail.supportRequired.map((service) => (
                      <span
                        key={service.id}
                        className="inline-flex items-center px-2 py-1 bg-[#3FC4E3]/10 text-[#3FC4E3] rounded-md text-xs font-popinsMedium"
                      >
                        {service.title}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="mb-8">
                <h3 className="font-popinsSemiBold pb-5 border-b border-border">
                  Contact Info
                </h3>
                <div className="flex flex-col lg:flex-row gap-8 mt-4">
                  <div>
                    <h3 className="font-popinsSemiBold mb-1">Email</h3>
                    <p className="font-popinsRegular text-[#61758A] text-sm mt-2">
                      {userDetail.email || "-"}
                    </p>
                  </div>
                  {userDetail.phoneNumber && (
                    <div>
                      <h3 className="font-popinsSemiBold mb-1">Phone</h3>
                      <p className="font-popinsRegular text-[#61758A] text-sm mt-2">
                        {userDetail.phoneNumber}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        <div className="lg:w-[50%] flex justify-center items-start p-4">
          {(() => {
            const profilePic = userDetail.documents?.find(
              (d) => d.documentType === "PROFILE_PICTURE" && d.documentLink
            );
            return profilePic ? (
              <img
                src={profilePic.documentLink}
                alt={organizationName}
                className="size-28 lg:size-36 rounded-full border-4 border-white shadow-lg object-cover"
              />
            ) : (
              <div
                className="size-28 lg:size-36 rounded-full border-4 border-white
                      bg-themeColor/10 text-themeColor flex items-center justify-center
                      text-4xl lg:text-5xl font-popinsSemiBold shadow-lg"
              >
                {getInitials(userDetail.firstName, userDetail.lastName)}
              </div>
            );
          })()}
        </div>
      </div>

      {userDetail.documents && userDetail.documents.length > 0 && (
        <div className="mb-8">
          <h3 className="font-popinsSemiBold mb-6">
            Documents & Compliance
          </h3>
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-popinsSemiBold text-gray-600 uppercase tracking-wider">
                      Document Name
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-popinsSemiBold text-gray-600 uppercase tracking-wider">
                      Upload Date
                    </th>
                    <th className="px-3 py-3 text-center text-xs font-popinsSemiBold text-gray-600 uppercase tracking-wider">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {userDetail.documents.map((doc) => {
                    const handleDownload = async (e) => {
                      e.preventDefault();
                      try {
                        const response = await fetch(doc.documentLink);
                        const blob = await response.blob();
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = doc.filename || 'document';
                        if (doc.mimeType) {
                          link.type = doc.mimeType;
                        }
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        window.URL.revokeObjectURL(url);
                      } catch (error) {
                        console.error('Error downloading file:', error);
                        toast.error('Failed to download file. Please try again.');
                      }
                    };

                    return (
                      <tr key={doc.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-3 py-4">
                          <div className="text-sm font-popinsMedium text-gray-900">
                            {doc.filename || doc.registrationDocumentText || "Document"}
                          </div>
                        </td>
                        <td className="px-3 py-4">
                          <div className="text-sm font-popinsMedium text-gray-500">
                            {formatDateTime(doc.createdAt)}
                          </div>
                        </td>
                        <td className="px-3 py-4 text-center">
                          {doc.documentLink && (
                            <button
                              onClick={handleDownload}
                              className="flex items-center gap-1 text-sm font-popinsMedium bg-sky-500 px-5 py-2 rounded hover:bg-sky-600 text-white mx-auto"
                            >
                              <Download size={16} /> Download
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {userDetail.activationStatus === "PENDING" && (
        <div className="flex gap-3 justify-end">
          <button
            onClick={handleApprove}
            disabled={isUpdating}
            className="flex items-center gap-2 bg-[#3FE36B] text-sm text-white px-6 py-2 rounded-md hover:bg-[#35C95A] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <CheckCircle2 size={18} className="text-white" />
            <span className="font-popinsSemiBold">
              {isUpdating ? "Approving..." : "Approve"}
            </span>
          </button>

          <button
            onClick={handleReject}
            disabled={isUpdating}
            className="flex items-center gap-2 bg-[#DD0000] text-sm text-white px-6 py-2 rounded-md hover:bg-[#BB0000] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <XCircle size={18} className="text-white" />
            <span className="font-popinsSemiBold">
              {isUpdating ? "Rejecting..." : "Reject"}
            </span>
          </button>
        </div>
      )}

      <RejectionModal
        isOpen={showRejectionModal}
        onClose={() => setShowRejectionModal(false)}
        onConfirm={handleRejectionConfirm}
      />
    </div>
  );
};

export default MainUserDetail;
